import { Box, Text } from "@chakra-ui/layout";
import { useAllocationRuleGraphVersionBasisPreviewTableDataRetrieve } from "gen/endpoints/allocation-rule-graph-version/allocation-rule-graph-version";
import React from "react";
import { useTranslation } from "react-i18next";
import { RefreshableTableBox } from "shared/components/table/box/RefreshableTableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";

export const AllocationBasisPreviewTable: React.FC<{
  allocationRuleGraphVersionId?: string;
  transactionCalculationId?: string;
  onRefresh: () => Promise<void>;
  requiresRefresh: boolean;
  isCustomRuleGraph: boolean;
}> = ({
  allocationRuleGraphVersionId,
  transactionCalculationId,
  onRefresh,
  isCustomRuleGraph,
  requiresRefresh,
}) => {
  const { t } = useTranslation(["allocation"]);
  const { fundFamilyId } = useRequiredStringParams();

  const { data: tableData } =
    useAllocationRuleGraphVersionBasisPreviewTableDataRetrieve(
      fundFamilyId,
      allocationRuleGraphVersionId!,
      {
        include_opt_out_badge: true,
        group_by_investor: true,
      },
      {
        query: {
          enabled: !!allocationRuleGraphVersionId,
        },
      },
    );

  return (
    <RefreshableTableBox
      sx={{
        flexGrow: 2,
        height: "100%",
      }}
      tableBoxProps={{
        isFullHeight: true,
      }}
      shouldRefresh={
        requiresRefresh && !!(transactionCalculationId || isCustomRuleGraph)
      }
      refreshTitle={t("allocation:graphVersionBasisTable.refresh")}
      onRefreshClick={async () => await onRefresh()}
    >
      <TableBoxHeader height="55px">
        <TableBoxTitle>
          {t("allocation:graphVersionBasisTable.title")}
        </TableBoxTitle>
        <DataTableBoxActions />
      </TableBoxHeader>
      <TableBoxBody>
        <Box
          display="flex"
          justifyContent="center"
          width="100%"
          minW="300px"
          p={tableData ? "0px" : "150px"}
        >
          {!allocationRuleGraphVersionId &&
            !transactionCalculationId &&
            !isCustomRuleGraph && (
              <Text>
                {t("allocation:graphVersionBasisTable.placeholderText")}
              </Text>
            )}
          {tableData && <DataTable data={tableData} />}
        </Box>
      </TableBoxBody>
    </RefreshableTableBox>
  );
};
